import React, { useState } from "react";
import { ref, update } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { DEFAULT_COST_PER_KG } from "../../../constants/farmerConstants";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import { maskPhoneNumber, maskIdentificationNumber } from "../../../utils/dataMasking";
import { useIsCompanyAdmin } from "../../../utils/useUserRole";

const EditFarmerModal = ({ farmer, companyName, onClose, onSave }) => {
  const [form, setForm] = useState({
    otherName: farmer.otherName || "",
    name: farmer.name || "",
    hat: farmer.hat || "Gold",
    house: farmer.house || "Weekly",
    phone: farmer.phone || "",
    location: farmer.address || "",
    idNumber: farmer.idNumber || "",
    costPerKg: farmer.costPerKg || DEFAULT_COST_PER_KG,
  });
  const [loading, setLoading] = useState(false);
  const isCompanyAdmin = useIsCompanyAdmin(companyName);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.name || !form.otherName) {
      toast.error("Name and User Name are required");
      return;
    }

    setLoading(true);
    try {
      const farmerPath = `Companies/${companyName}/ValidatedFarmers/${form.otherName}`;
      const updatedFarmer = {
        CloudName: form.name,
        Hat: form.hat,
        House: form.house,
        PhoneNumber: form.phone || "",
        Address: form.location || "",
        IdentificationNumber: form.idNumber || "",
        CostPerKg: parseFloat(form.costPerKg) || DEFAULT_COST_PER_KG,
      };

      await update(ref(database, farmerPath), updatedFarmer);

      await logRecordAuditEvent({
        action: "edit",
        entityType: "farmer",
        companyName,
        recordId: form.otherName || farmer?.otherName,
        sourceComponent: "EditFarmerModal",
        oldData: farmer,
        newData: updatedFarmer,
      });

      toast.success("Farmer updated successfully!");
      onSave && onSave();
      onClose();
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Failed to update farmer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-xl p-6 shadow-xl w-96 relative">
        <h2 className="text-xl font-semibold mb-4 text-cyan-700">
          Edit Farmer Details
        </h2>

        <div className="space-y-3">
          {/* Name */}
          <input
            name="name"
            type="text"
            placeholder="Cloud Name"
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            value={form.name}
            onChange={handleChange}
          />

          {/* Location */}
          <input
            name="location"
            type="text"
            placeholder="Location"
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            value={form.location}
            onChange={handleChange}
          />

          {/* Phone */}
          <input
            name="phone"
            type="text"
            placeholder="Phone Number"
            className={`border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500 ${!isCompanyAdmin ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
            value={isCompanyAdmin ? form.phone : maskPhoneNumber(form.phone)}
            onChange={isCompanyAdmin ? handleChange : undefined}
            readOnly={!isCompanyAdmin}
            title={!isCompanyAdmin ? "Only CompanyAdmin can edit the phone number" : undefined}
          />

          {/* ID Number */}
          <input
            name="idNumber"
            type="text"
            placeholder="ID Number"
            className={`border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500 ${!isCompanyAdmin ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
            value={isCompanyAdmin ? form.idNumber : maskIdentificationNumber(form.idNumber)}
            onChange={isCompanyAdmin ? handleChange : undefined}
            readOnly={!isCompanyAdmin}
            title={!isCompanyAdmin ? "Only CompanyAdmin can edit the ID number" : undefined}
          />

          {/* Cost Per Kg */}
          <input
            name="costPerKg"
            type="number"
            placeholder={`Cost Per Kg (KES, default: ${DEFAULT_COST_PER_KG})`}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            value={form.costPerKg}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

          {/* Hat & House (Payment Span) — side by side at the bottom */}
          <div className="flex justify-between items-center space-x-2">
            <select
              name="hat"
              value={form.hat}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-1/2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option>Gold</option>
              <option>Silver</option>
              <option>Bronze</option>
            </select>

            <select
              name="house"
              value={form.house}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-1/2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option>Weekly</option>
              <option>2 - weeks</option>
              <option>Monthly</option>
            </select>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex justify-end gap-3">
            <button
              className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded text-white transition bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFarmerModal;
